import { useState, useEffect, useRef } from 'react';
import { Loader2, Sparkles, CheckCircle, Link as LinkIcon, Key, Plus, ExternalLink, RefreshCw, Info } from 'lucide-react';

import { AppListItem, App, Connection } from '@optidev/shared-types';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import { useAuth } from '@/contexts/AuthContext';
import { appsApi } from '@/lib/api/apps';
import { connectionsApi } from '@/lib/api/connections';
import { projectApi as supabaseApi } from '@/lib/api/supabase/project.api';
import { REGION_CONFIG } from '@/lib/constants/regions';
import { cn } from '@/lib/utils';

interface RemixAppModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  app: AppListItem;
  onSuccess: (projectId: string) => void;
  /**
   * Arriving via the "Try in OptiDev" deep link (?remix=<id>). When the app needs
   * no user input, auto-remix and drop straight into the project instead of
   * showing the form and waiting for a click. Falls back to the form if the app
   * requires connections/secrets, or if the auto-remix errors.
   */
  autoStart?: boolean;
}

export function RemixAppModal({
  open,
  onOpenChange,
  app,
  onSuccess,
  autoStart = false,
}: RemixAppModalProps) {
  const { getIdToken } = useAuth();
  const autoStartedRef = useRef(false);

  // Form state
  const [projectName, setProjectName] = useState('');
  const [selectedConnections, setSelectedConnections] = useState<Record<string, string>>({});
  const [secrets, setSecrets] = useState<Record<string, string>>({});

  // Supabase region and secrets state
  const [regions, setRegions] = useState<string[]>([]);
  const [selectedRegion, setSelectedRegion] = useState('us-east-2');
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [supabaseSecrets, setSupabaseSecrets] = useState<Record<string, string>>({});

  // Data state
  const [fullApp, setFullApp] = useState<App | null>(null);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loadingApp, setLoadingApp] = useState(false);

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load full app details, connections, and generate name when modal opens
  useEffect(() => {
    if (open && app) {
      loadAppAndConnections();
    }
  }, [open, app]);

  // Load available regions when Supabase is required with auto-activate
  useEffect(() => {
    const loadRegions = async () => {
      if (!open || !fullApp?.config.supabase?.autoActivate) return;
      setLoadingRegions(true);
      try {
        const token = await getIdToken();
        if (!token) return;
        const data = await supabaseApi.getRegions(token);
        setRegions(data);
        if (data.length > 0 && !data.includes(selectedRegion)) {
          setSelectedRegion(data.includes('us-east-2') ? 'us-east-2' : data[0]);
        }
      } catch (error) {
        console.error('Failed to load regions:', error);
        // Fallback to all known regions
        setRegions(Object.keys(REGION_CONFIG));
      } finally {
        setLoadingRegions(false);
      }
    };
    loadRegions();
  }, [open, fullApp?.config.supabase?.autoActivate]);

  // Register callback for child window to notify when connection is created
  useEffect(() => {
    if (open) {
      (window as Window & { onConnectionCreated?: (connectionId: string, reqKey: string) => void }).onConnectionCreated = async (connectionId: string, reqKey: string) => {
        // Refresh connections list
        await loadAppAndConnections();
        // Auto-select the newly created connection using reqKey directly
        // This handles the case where an app has multiple connections of the same type
        setSelectedConnections(prev => ({ ...prev, [reqKey]: connectionId }));
      };
    }
    return () => {
      delete (window as Window & { onConnectionCreated?: (connectionId: string, reqKey: string) => void }).onConnectionCreated;
    };
  }, [open]);

  const loadAppAndConnections = async () => {
    try {
      setLoadingApp(true);
      setError(null);
      const token = await getIdToken();
      if (!token) return;

      const [appData, connectionsData] = await Promise.all([
        appsApi.getOne(token, app.id),
        connectionsApi.getAll(token),
      ]);

      setFullApp(appData);
      setConnections(connectionsData);

      // Generate name: app name + random 5-digit suffix for uniqueness
      const suffix = Math.floor(10000 + Math.random() * 90000);
      setProjectName(`${app.name}-${suffix}`);

      // Initialize selected connections
      const initialConnections: Record<string, string> = {};
      appData.connections.forEach((req) => {
        initialConnections[req.key] = '';
      });
      setSelectedConnections(initialConnections);

      // Initialize secrets
      const initialSecrets: Record<string, string> = {};
      appData.config.envVariables?.forEach((env) => {
        initialSecrets[env.key] = env.defaultValue || '';
      });
      setSecrets(initialSecrets);

      // Initialize supabase secrets
      const initialSupabaseSecrets: Record<string, string> = {};
      appData.config.supabase?.secrets?.forEach((secret) => {
        initialSupabaseSecrets[secret.key] = '';
      });
      setSupabaseSecrets(initialSupabaseSecrets);
    } catch (err) {
      console.error('Failed to load app details:', err);
      setError('Failed to load app details');
    } finally {
      setLoadingApp(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitRemix();
  };

  const submitRemix = async () => {
    setError(null);
    setIsLoading(true);

    try {
      const token = await getIdToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      // Filter out empty connections
      const filteredConnections: Record<string, string> = {};
      Object.entries(selectedConnections).forEach(([key, value]) => {
        if (value) {
          filteredConnections[key] = value;
        }
      });

      // Filter out empty secrets
      const filteredSecrets: Record<string, string> = {};
      Object.entries(secrets).forEach(([key, value]) => {
        if (value) {
          filteredSecrets[key] = value;
        }
      });

      // Validate required Supabase secrets
      if (fullApp?.config.supabase?.autoActivate && fullApp.config.supabase.secrets) {
        for (const secret of fullApp.config.supabase.secrets) {
          if (secret.required && !supabaseSecrets[secret.key]) {
            setError(`Required secret "${secret.label}" is missing`);
            setIsLoading(false);
            return;
          }
        }
      }

      // Filter out empty supabase secrets
      const filteredSupabaseSecrets: Record<string, string> = {};
      Object.entries(supabaseSecrets).forEach(([key, value]) => {
        if (value) {
          filteredSupabaseSecrets[key] = value;
        }
      });

      const result = await appsApi.remix(token, app.id, {
        projectName: projectName.trim(),
        connections: Object.keys(filteredConnections).length > 0 ? filteredConnections : undefined,
        secrets: Object.keys(filteredSecrets).length > 0 ? filteredSecrets : undefined,
        autoActivateSupabase: fullApp?.config.supabase?.autoActivate,
        supabaseRegion: fullApp?.config.supabase?.autoActivate ? selectedRegion : undefined,
        supabaseSecrets: fullApp?.config.supabase?.autoActivate && Object.keys(filteredSupabaseSecrets).length > 0
          ? filteredSupabaseSecrets
          : undefined,
      });

      setIsLoading(false);
      setIsSuccess(true);

      // Show success briefly then navigate
      setTimeout(() => {
        onSuccess(result.id);
      }, 1000);
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { message?: string } } };
      setError(axiosError?.response?.data?.message || 'Failed to remix app');
      setIsLoading(false);
    }
  };

  const resetAndClose = () => {
    setProjectName('');
    setSelectedConnections({});
    setSecrets({});
    setSupabaseSecrets({});
    setSelectedRegion('us-east-2');
    setError(null);
    setIsSuccess(false);
    setFullApp(null);
    autoStartedRef.current = false;
    onOpenChange(false);
  };

  const handleClose = () => {
    if (!isLoading && !isSuccess) {
      resetAndClose();
    }
  };

  // Get compatible connections for a requirement
  // Note: Connection.type contains the connector type (e.g., "google-sheets"),
  // which matches the connectorId from the app's connection requirements
  const getCompatibleConnections = (connectorId: string): Connection[] => {
    return connections.filter((c) => c.type === connectorId && c.enabled);
  };

  // Extract connector type name from label (e.g., "Google Sheets (email)" -> "Google Sheets")
  const getConnectorTypeName = (label: string): string => {
    const match = label.match(/^([^(]+)/);
    return match ? match[1].trim() : label;
  };

  // Check if form is valid
  const isFormValid = (): boolean => {
    if (!projectName.trim()) return false;

    // Check required connections
    const hasRequiredConnections = fullApp?.connections
      .filter((req) => req.required)
      .every((req) => selectedConnections[req.key]);

    // Check required secrets
    const hasRequiredSecrets = fullApp?.config.envVariables
      ?.filter((env) => env.required)
      .every((env) => secrets[env.key]);

    // Check required Supabase secrets
    const hasRequiredSupabaseSecrets = fullApp?.config.supabase?.secrets
      ?.filter((secret) => secret.required)
      .every((secret) => supabaseSecrets[secret.key]);

    return (hasRequiredConnections ?? true) && (hasRequiredSecrets ?? true) && (hasRequiredSupabaseSecrets ?? true);
  };

  // Auto-remix on deep link once the app has loaded and nothing needs the user.
  // Guarded by a ref so it fires exactly once. If the app requires input (form
  // invalid) or the remix errors, we fall through to the form below.
  useEffect(() => {
    if (
      autoStart &&
      open &&
      fullApp &&
      !loadingApp &&
      !isLoading &&
      !isSuccess &&
      !error &&
      !autoStartedRef.current &&
      isFormValid()
    ) {
      autoStartedRef.current = true;
      submitRemix();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, open, fullApp, loadingApp, isLoading, isSuccess, error]);

  // Auto-remix in progress (deep link): show a clean loading panel, never the
  // form. Only while there's no error and either still loading or the form is
  // valid (i.e. we're about to / already auto-submitting).
  const autoRunning =
    autoStart && !error && !isSuccess && (!fullApp || loadingApp || isLoading || isFormValid());
  if (autoRunning) {
    return (
      <Dialog open={open} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-[450px]">
          <div className="flex flex-col items-center justify-center py-10">
            <Loader2 className="h-8 w-8 animate-spin text-purple-600 mb-4" />
            <p className="text-lg font-medium">Setting up {app.name}…</p>
            <p className="text-sm text-muted-foreground">Building your project and dropping you in.</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Success screen
  if (isSuccess) {
    return (
      <Dialog open={open} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-[450px]">
          <div className="flex flex-col items-center justify-center py-8">
            <div className="rounded-full bg-green-100 dark:bg-green-900/30 p-3 mb-4">
              <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <p className="text-lg font-medium">Creating your project...</p>
            <p className="text-sm text-muted-foreground">Setting up {projectName}</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Use {app.name}
          </DialogTitle>
          <DialogDescription>
            Create your own copy of this app
          </DialogDescription>
        </DialogHeader>

        {loadingApp ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Project Name */}
            <div className="space-y-2">
              <Label htmlFor="projectName">Project Name *</Label>
              <Input
                id="projectName"
                type="text"
                placeholder="My Awesome App"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                disabled={isLoading}
                autoComplete="off"
              />
            </div>

            {/* Connection Requirements */}
            {fullApp && fullApp.connections.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <LinkIcon className="h-4 w-4" />
                    Connections
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={loadAppAndConnections}
                    disabled={loadingApp}
                    title="Refresh connections"
                  >
                    <RefreshCw className={cn("h-3.5 w-3.5", loadingApp && "animate-spin")} />
                  </Button>
                </div>
                {fullApp.connections.map((req) => {
                  const compatible = getCompatibleConnections(req.connectorId);
                  const connectorType = getConnectorTypeName(req.label);
                  const isLongLabel = req.label.length > 40;
                  return (
                    <div key={req.key} className="space-y-1.5">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Label className="text-sm inline-block max-w-full truncate cursor-default">
                              {isLongLabel ? connectorType : req.label}
                              {req.required && <span className="text-red-500 ml-0.5">*</span>}
                            </Label>
                          </TooltipTrigger>
                          {isLongLabel && (
                            <TooltipContent>
                              <p>{req.label}</p>
                            </TooltipContent>
                          )}
                        </Tooltip>
                      </TooltipProvider>
                      {req.description && (
                        <p className="text-xs text-muted-foreground">{req.description}</p>
                      )}
                      <Select
                        value={selectedConnections[req.key] || ''}
                        onValueChange={(value) => {
                          if (value === '__create_new__') {
                            // Open connectors page in new tab with auto-open modal for this connector
                            // Pass reqKey so the callback knows which dropdown to auto-select
                            window.open(
                              `/dashboard/connectors?tab=connectors&connector=${req.connectorId}&reqKey=${req.key}`,
                              '_blank'
                            );
                            return;
                          }
                          setSelectedConnections((prev) => ({ ...prev, [req.key]: value }));
                        }}
                        disabled={isLoading}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a connection" />
                        </SelectTrigger>
                        <SelectContent>
                          {/* Create new option - always first */}
                          <SelectItem value="__create_new__" className="text-primary">
                            <span className="flex items-center gap-2">
                              <Plus className="h-4 w-4" />
                              Create new connection
                              <ExternalLink className="h-3 w-3 ml-auto opacity-50" />
                            </span>
                          </SelectItem>
                          {compatible.length > 0 && <SelectSeparator />}
                          {/* Existing connections */}
                          {compatible.map((conn) => (
                            <SelectItem key={conn.id} value={conn.id}>
                              {conn.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {compatible.length === 0 && (
                        <p className="text-xs text-amber-600 dark:text-amber-400">
                          You need to create a {getConnectorTypeName(req.label)} connection first
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Environment Variables / Secrets */}
            {fullApp?.config.envVariables && fullApp.config.envVariables.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Key className="h-4 w-4" />
                  Configuration
                </div>
                {fullApp.config.envVariables.map((env) => (
                  <div key={env.key} className="space-y-1.5">
                    <Label className="text-sm">
                      {env.label}
                      {env.required && <span className="text-red-500 ml-0.5">*</span>}
                    </Label>
                    {env.description && (
                      <p className="text-xs text-muted-foreground">{env.description}</p>
                    )}
                    <Input
                      type={env.masked ? 'password' : 'text'}
                      placeholder={env.defaultValue || `Enter ${env.label}`}
                      value={secrets[env.key] || ''}
                      onChange={(e) =>
                        setSecrets((prev) => ({ ...prev, [env.key]: e.target.value }))
                      }
                      disabled={isLoading}
                      autoComplete="off"
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Supabase Notice and Configuration */}
            {fullApp?.config.supabase?.required && (
              <div className="space-y-4">
                <Alert>
                  <AlertDescription className="text-sm">
                    This app requires OptiDev Cloud.
                    {fullApp.config.supabase.autoActivate
                      ? ' It will be activated automatically.'
                      : ' You will need to activate it after project creation.'}
                  </AlertDescription>
                </Alert>

                {fullApp.config.supabase.autoActivate && (
                  <>
                    {/* Region Selector */}
                    <div className="space-y-2">
                      <Label htmlFor="supabase-region">Select Region</Label>
                      <Select
                        value={selectedRegion}
                        onValueChange={setSelectedRegion}
                        disabled={isLoading || loadingRegions}
                      >
                        <SelectTrigger id="supabase-region">
                          <SelectValue>
                            {loadingRegions ? (
                              <span className="flex items-center gap-2">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Loading regions...
                              </span>
                            ) : (
                              <span className="flex items-center gap-2">
                                <span>{REGION_CONFIG[selectedRegion]?.flag || '🌐'} {REGION_CONFIG[selectedRegion]?.name || selectedRegion}</span>
                                <span className="font-mono text-xs text-muted-foreground">{REGION_CONFIG[selectedRegion]?.id || selectedRegion}</span>
                              </span>
                            )}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {regions.map((r) => {
                            const info = REGION_CONFIG[r] || { name: r, flag: '🌐' };
                            return (
                              <SelectItem key={r} value={r}>
                                <span className="flex items-center gap-2">
                                  {info.flag} {info.name}
                                  <span className="font-mono text-xs text-muted-foreground">{r}</span>
                                </span>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                        <Info className="w-3 h-3" />
                        Choose a region close to your users for optimal performance
                      </p>
                    </div>

                    {/* Supabase Secrets (for Edge Functions) */}
                    {fullApp.config.supabase.secrets && fullApp.config.supabase.secrets.length > 0 && (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Key className="h-4 w-4 text-muted-foreground" />
                          <Label className="text-sm font-medium">Edge Function Secrets</Label>
                        </div>
                        {fullApp.config.supabase.secrets.map((secret) => (
                          <div key={secret.key} className="space-y-1.5">
                            <Label htmlFor={`supabase-secret-${secret.key}`} className="text-sm">
                              {secret.label}
                              {secret.required && <span className="text-red-500 ml-0.5">*</span>}
                            </Label>
                            {secret.description && (
                              <p className="text-xs text-muted-foreground">{secret.description}</p>
                            )}
                            <Input
                              id={`supabase-secret-${secret.key}`}
                              type="password"
                              placeholder={`Enter ${secret.label}`}
                              value={supabaseSecrets[secret.key] || ''}
                              onChange={(e) =>
                                setSupabaseSecrets((prev) => ({
                                  ...prev,
                                  [secret.key]: e.target.value,
                                }))
                              }
                              disabled={isLoading}
                              autoComplete="off"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Error */}
            {error && (
              <Alert variant="destructive">
                <AlertDescription className="text-sm">{error}</AlertDescription>
              </Alert>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!isFormValid() || isLoading}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    Use App
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
